import React, { useRef, useState } from 'react'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { buildNewColumn, kindOptionsToSave } from 'components/spreadsheet/helpers/functions'
import { createTableColumnViewAction, ICreateTableColumnView } from 'components/spreadsheet/contexts/data/actions'
import { SpreadsheetReducerActions, ISpreadsheetData } from 'components/spreadsheet/types'
import { ITableViewColumn, IContextMenuState } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { SELECT_COLUMN_TYPES } from 'components/spreadsheet/constants/const'

// A column not created yet, as the create endpoint takes it.
export type ColumnDraft = ICreateTableColumnView

export interface NewColumn {
  menuState: IContextMenuState
  setMenuState: React.Dispatch<React.SetStateAction<IContextMenuState>>
  // The column being set up, a Text column until its type is changed. Null while nothing is being added.
  draft: ColumnDraft | null
  processing: boolean
  // Open the Edit column panel at `anchor` for a column that would go in at `sortOrder`. Nothing is created yet.
  open: (sortOrder: number, anchor: { top: string; left: string }) => void
  // Create the column as it was set up in the panel, then close it.
  submit: (column: ITableViewColumn) => void
  // Close the panel without creating anything.
  close: () => void
}

// Adding a column, from the header's + button or a column's Insert column before: the Edit column panel
// opens on a draft of it, and the column is only created on Add column, in one request with its options, so it's
// created whole or not at all. Closing the panel creates nothing.
export const useNewColumn = (
  spreadsheetData: ISpreadsheetData,
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>
): NewColumn => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()

  const [processing, setProcessing] = useState<boolean>(false)
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [draft, setDraft] = useState<ColumnDraft | null>(null)

  // What the panel was opened from (the + button, say), to give the keyboard back to when it closes.
  const openerRef = useRef<HTMLElement | null>(null)

  const close = (): void => {
    setMenuState(INITIAL_CONTEXT_MENU_STATE)
    setDraft(null)
    // Unless the keyboard has gone somewhere else meanwhile (a press elsewhere), and while the opener is still there
    // (a column menu's row isn't, once its menu has closed).
    const opener = openerRef.current
    openerRef.current = null
    const focused = document.activeElement
    const inPanelOrNowhere = !focused || focused === document.body || !!focused.closest('[data-menu]')
    if (opener?.isConnected && inPanelOrNowhere) opener.focus()
  }

  const open = (sortOrder: number, anchor: { top: string; left: string }): void => {
    if (processing) return
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setDraft(
      buildNewColumn(
        spreadsheetData,
        sortOrder,
        project.publicId,
        project.defaultHeaderBackgroundColor,
        project.defaultHeaderTextColor
      )
    )
    setMenuState({ ...INITIAL_CONTEXT_MENU_STATE, open: true, top: anchor.top, left: anchor.left })
  }

  const submit = (column: ITableViewColumn): void => {
    if (processing) return
    const { kindOptions, ...rest } = column
    // The panel's stand-ins for a saved column's own fields, which the create endpoint doesn't take.
    const fields: Partial<ITableViewColumn> = { ...rest }
    delete fields.publicId
    delete fields.isSynced
    const toCreate = {
      ...fields,
      ...(SELECT_COLUMN_TYPES.includes(column.kind) && kindOptions
        ? { kindOptions: kindOptionsToSave(kindOptions) }
        : {})
    } as ColumnDraft

    setProcessing(true)
    createTableColumnViewAction(
      spreadsheetData.viewDetails.publicId,
      toCreate,
      setSpreadsheetData,
      () => {
        setProcessing(false)
        close()
      },
      // Nothing was created: the panel stays open on the draft, to put right and add again.
      (error: any) => {
        setProcessing(false)
        displayErrorMessage(error)
      }
    )
  }

  return { menuState, setMenuState, draft, processing, open, submit, close }
}
